import React, {useCallback, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {flowResult} from 'mobx';
import {App} from 'antd';

import type {BillingCycleType, BillingEditionType, BillingProductType, ICreateOrderData} from '../../api/billing';
import EnterpriseContactModal, {type IEnterpriseContactValues} from './EnterpriseContactModal';
import PurchasePointsModal from './PurchasePointsModal';
import SubscriptionModal from './SubscriptionModal';
import PaymentQRModal from './PaymentQRModal';
import type {ISubscriptionPlanView, IPointsPackagePlanView} from '../../stores/BillingStore';

interface IQrOrderState {
    orderId: string;
    goodsId: string;
    orderType?: BillingCycleType;
    product: BillingProductType;
    quantity?: number;
    payUrl: string;
    price?: string;
}

interface IBillingModalHostProps {
    store: IPageStore;
}

const BillingModalHost: React.FC<IBillingModalHostProps> = ({store}) => {
    const {message} = App.useApp();
    const {billingStore} = store;
    // 二维码支付弹窗（Pro 订单支付宝扫码）
    const [qrOrder, setQrOrder] = useState<IQrOrderState | null>(null);

    const handleLoadSubscriptionPlans = useCallback(
        async (edition: BillingEditionType, cycle: BillingCycleType) => {
            try {
                await flowResult(billingStore.fetchSubscriptionPlans(edition, cycle));
            }
            catch (error) {
                const err = error as Error;
                message.error(err.message || '获取订阅套餐失败，请稍后重试');
                throw err;
            }
        },
        [billingStore, message]
    );

    const handleLoadEnterprisePlans = useCallback(
        async () => {
            try {
                await flowResult(billingStore.fetchEnterprisePlans());
            }
            catch (error) {
                const err = error as Error;
                message.error(err.message || '获取企业版套餐失败，请稍后重试');
                throw err;
            }
        },
        [billingStore, message]
    );

    const handleSubscribe = async (plan: ISubscriptionPlanView, cycle: BillingCycleType) => {
        const isEnableAutoRenewRule = billingStore.shouldEnableAutoRenewByRule(plan);

        try {
            if (isEnableAutoRenewRule) {
                const {serviceId, serviceType, region} = plan;
                if (!serviceId || !serviceType || !region) {
                    message.error('缺少开启连续包月所需参数');
                    return;
                }

                await flowResult(billingStore.enableAutoRenew({
                    serviceId,
                    serviceType,
                    region
                }));
                await flowResult(billingStore.fetchSubscriptionPlans('personal', cycle));
                message.success('已开启连续包月');
                return;
            }

            const result = await flowResult(billingStore.createSubscription(plan.id, cycle)) as ICreateOrderData | undefined;
            billingStore.closeSubscriptionModal();

            // 二维码支付：打开扫码弹窗，支付结果轮询获取
            if (result?.qrcode && result.pay_url) {
                setQrOrder({
                    orderId: result.order_id,
                    goodsId: plan.id,
                    orderType: cycle,
                    product: 'ws',
                    payUrl: result.pay_url,
                    price: plan.priceText
                });
                return;
            }

            if (result?.pay_url) {
                window.open(result.pay_url, '_blank', 'noopener,noreferrer');
            }

            await Promise.all([
                flowResult(billingStore.fetchOverview()),
                flowResult(billingStore.fetchOrderRecords())
            ]);
            message.success('已创建订阅订单，请完成支付');
        }
        catch (error) {
            const err = error as Error;
            message.error(err.message || (isEnableAutoRenewRule ? '开启连续包月失败，请稍后重试' : '创建订阅订单失败，请稍后重试'));
            throw err;
        }
    };

    const handleLoadPointsPackages = useCallback(
        async () => {
            try {
                await flowResult(billingStore.fetchPointsPackages());
            }
            catch (error) {
                const err = error as Error;
                message.error(err.message || '获取资源点包失败，请稍后重试');
                throw err;
            }
        },
        [billingStore, message]
    );

    const handlePurchasePoints = async (id: string, quantity: number) => {
        try {
            const result = await flowResult(billingStore.createPointsPurchase(id, quantity)) as ICreateOrderData | undefined;
            billingStore.closePurchaseModal();

            // 二维码支付：打开扫码弹窗（含协议 + 遮罩），支付结果轮询获取
            if (result?.qrcode && result.pay_url) {
                const plan = billingStore.pointsPackages.find((item: IPointsPackagePlanView) => item.id === id);
                const unitPrice = plan?.packagePrice?.replace(/^¥\s*/, '');
                const totalPrice = unitPrice ? `${Number(unitPrice) * quantity}` : undefined;
                setQrOrder({
                    orderId: result.order_id,
                    goodsId: id,
                    product: 'ws',
                    quantity,
                    payUrl: result.pay_url,
                    price: totalPrice
                });
                return;
            }

            if (result?.pay_url) {
                window.open(result.pay_url, '_blank', 'noopener,noreferrer');
            }

            await Promise.all([
                flowResult(billingStore.fetchOverview()),
                flowResult(billingStore.fetchPointsRecords()),
                flowResult(billingStore.fetchOrderRecords())
            ]);
            message.success('已创建资源点订单，请完成支付');
        }
        catch (error) {
            const err = error as Error;
            message.error(err.message || '创建资源点订单失败，请稍后重试');
            throw err;
        }
    };

    const handleContactSubmit = async (values: IEnterpriseContactValues) => {
        try {
            await flowResult(billingStore.submitEnterpriseContact({
                industry: values.industry,
                contact_name: values.contactName,
                contact_info: values.contactInfo,
                company_name: values.companyName,
                scenario: values.scenario
            }));
            billingStore.closeContactModal();
            message.success('提交成功，我们将尽快与您联系');
        }
        catch (error) {
            const err = error as Error;
            message.error(err.message || '提交失败，请稍后重试');
            throw err;
        }
    };

    const handleCancelAutoRenew = async (params: {
        serviceId: string;
        serviceType: string;
        region: string;
    }) => {
        try {
            await flowResult(billingStore.cancelAutoRenew(params));
            message.success('已取消连续包月');
        }
        catch (error) {
            const err = error as Error;
            message.error(err.message || '取消连续包月失败，请稍后重试');
            throw err;
        }
    };

    // 二维码支付成功：关闭弹窗并刷新账户信息（account_info / quotas / 账单）
    const handleQrPaid = async () => {
        setQrOrder(null);
        await Promise.all([
            // quotas（额度总览）
            flowResult(billingStore.fetchOverview()),
            // 账单信息（bills）
            flowResult(billingStore.fetchPointsRecords()),
            // 订单记录
            flowResult(billingStore.fetchOrderRecords()),
            // account_info（账户信息）
            flowResult(store.rootStore.userStore.fetchAccountInfo())
        ]);
    };

    return (
        <>
            <SubscriptionModal
                open={billingStore.showSubscriptionModal}
                plans={billingStore.subscriptionPlans}
                enterprisePlans={billingStore.enterprisePlans}
                cycleOptions={billingStore.subscriptionCycleOptions}
                loading={billingStore.subscriptionPlansLoading}
                submitting={billingStore.subscriptionSubmitting}
                onCancel={billingStore.closeSubscriptionModal}
                onEnterpriseContact={billingStore.openContactModal}
                onLoadPlans={handleLoadSubscriptionPlans}
                onLoadEnterprisePlans={handleLoadEnterprisePlans}
                onSubscribe={handleSubscribe}
                onCancelAutoRenew={handleCancelAutoRenew}
            />

            <PurchasePointsModal
                open={billingStore.showPurchaseModal}
                loading={billingStore.pointsPackagesLoading}
                submitting={billingStore.purchaseSubmitting}
                plans={billingStore.pointsPackages}
                onCancel={billingStore.closePurchaseModal}
                onContactUs={billingStore.openContactFromPurchase}
                onLoadPlans={handleLoadPointsPackages}
                onPurchase={handlePurchasePoints}
            />

            <EnterpriseContactModal
                open={billingStore.showContactModal}
                submitting={billingStore.contactSubmitting}
                onCancel={billingStore.closeContactModal}
                onSubmit={handleContactSubmit}
            />

            {qrOrder && (
                <PaymentQRModal
                    open={!!qrOrder}
                    orderId={qrOrder.orderId}
                    goodsId={qrOrder.goodsId}
                    orderType={qrOrder.orderType}
                    product={qrOrder.product}
                    quantity={qrOrder.quantity}
                    payUrl={qrOrder.payUrl}
                    price={qrOrder.price}
                    onClose={() => setQrOrder(null)}
                    onPaid={handleQrPaid}
                />
            )}
        </>
    );
};

export default observer(BillingModalHost);
